import { useEffect, useState } from 'react';
import { Button, LinearProgress, Tabs, SelectMultiple } from '../../tailwindcomponents';
import ShortTermHangarPlanTab from './ShortTermHangarPlanTab';
import LongTermHangarPlanTab from './LongTermHangarPlanTab';
import { hangarPlanLineOptions, workTypeOptions } from '../../properties/lineProperties';
import hangarPlanApi from '../../api/hangarPlanApi';
import { EventInfo } from '../../types/mobiScroll';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { DateTime } from 'luxon';
import { useQueryTab } from '../../utils/usePersistedTab';
import { safeLocalStorage } from '../../utils/safeLocalStorage';
import { palette } from '../../theme/palette';

const statusOptions = [
    { id: 0, name: "COMPLETE" },
    { id: 1, name: "ACTV" },
    { id: 2, name: "COMMIT" },
    { id: 3, name: "IN WORK" },
];

// Helper function to get default hangars based on user role
const getDefaultHangarFilters = (isLinePlanner: boolean): string[] => {
    return isLinePlanner 
        ? [hangarPlanLineOptions.kefHangarBay1, hangarPlanLineOptions.kefHangarBay2, hangarPlanLineOptions.kefHangarBay7] 
        : [hangarPlanLineOptions.rkvLine, hangarPlanLineOptions.kefHangarBay3, hangarPlanLineOptions.kefHangarBay4, hangarPlanLineOptions.kefHangarBay5];
};

const defaultStatusFilters = [2];

const workTypeFilterOptions = [
    { id: 0, name: workTypeOptions.LMTARMAC },
    { id: 1, name: workTypeOptions.LMHANGAR },
    { id: 2, name: workTypeOptions.HEAVY },
];

const getHangarFiltersFromLocalStorage = (isLinePlanner: boolean): string[] => {
    return safeLocalStorage.getJson<string[]>("hangarFilters", getDefaultHangarFilters(isLinePlanner));
};

const getStatusFiltersFromLocalStorage = (): number[] => {
    return safeLocalStorage.getJson<number[]>("statusFilters", defaultStatusFilters);
};

const getWorkTypeFiltersFromLocalStorage = (): number[] => {
    return safeLocalStorage.getJson<number[]>("workTypeFilters", []);
};

export const HangarPlan: React.FC = () => {
    // Call useSelector inside the component
    const isLinePlanner = useSelector((state: RootState) => 
        state.user.me.user_clearence_groups.includes("Line Planner Edit") || 
        state.user.me.user_clearence_groups.includes("Line Planner View")
    );
    const { tabValue, setTabValue } = useQueryTab("tab", 0);
    const [loading, setLoading] = useState(false);
    const [calendarData, setCalendarData] = useState<EventInfo[]>([]);

    const [selectedHangars, setSelectedHangars] = useState<string[]>(() => {
        return getHangarFiltersFromLocalStorage(isLinePlanner);
    });

    const [selectedStatusOptions, setSelectedStatusOptions] = useState<number[]>(() => {
        return getStatusFiltersFromLocalStorage();
    });

    const [selectedWorkTypes, setSelectedWorkTypes] = useState<number[]>(() => {
        return getWorkTypeFiltersFromLocalStorage();
    });

    // Initialize localStorage with defaults if not set
    useEffect(() => {
        const storedStatus = safeLocalStorage.get("statusFilters");
        const storedHangars = safeLocalStorage.get("hangarFilters");

        if (storedStatus === null || storedStatus === undefined) {
            safeLocalStorage.setJson("statusFilters", defaultStatusFilters);
        }

        if (storedHangars === null || storedHangars === undefined) {
            safeLocalStorage.setJson("hangarFilters", getDefaultHangarFilters(isLinePlanner));
        }
    }, [isLinePlanner]);

    useEffect(() => {
        refresh();
    }, []);

    const refresh = async () => {
        setLoading(true);

        let fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 90);
        let toDate = new Date();
        toDate.setDate(toDate.getDate() + 90);

        const tempSelectedStatus: string[] = selectedStatusOptions.map(id => statusOptions.find(o => o.id === id)!.name);
        const tempSelectedWorkTypes: string[] = selectedWorkTypes.map(id => workTypeFilterOptions.find(o => o.id === id)!.name);

        await hangarPlanApi.GetHangarPlanPackages(fromDate, toDate, tempSelectedStatus, selectedHangars, false, tempSelectedWorkTypes).then(res => {
            setLoading(false);
            const data = res.data;
            const tasks: EventInfo[] = [];
            data.forEach(packet => {
                const startDate = new Date(packet.start_date);
                const endDate = new Date(packet.end_date);

                const startDt = DateTime.fromJSDate(startDate);
                const endDt   = DateTime.fromJSDate(endDate);

                const formattedStart = startDt.toFormat('dd.MM.yyyy HH:mm');
                const formattedEnd   = endDt.toFormat('dd.MM.yyyy HH:mm');

                // Adjust startDate to the nearest 4-hour block
                const startHour = startDate.getHours();
                const adjustedStartHour = Math.floor(startHour / 4) * 4;
                startDate.setHours(adjustedStartHour, 0, 0, 0);

                // Adjust endDate to the nearest 4-hour block
                let endHour = endDate.getHours();
                if (startHour === endHour) {
                    endHour = endHour + 1;
                }
                const adjustedEndHour = Math.ceil((endHour + endDate.getMinutes() / 60) / 4) * 4;
                endDate.setHours(adjustedEndHour, 0, 0, 0);
                tasks.push({
                    "start": startDate,
                    "end": endDate,
                    "id": packet.wo_barcode,
                    "title": packet.tail_number + " " + packet.wo_name,
                    "resource": packet.wo_location,
                    "color": ReturnStatusColor(packet.wo_status, undefined),
                    "wo_status": packet.wo_status,
                    "parts_amounts": packet.basket,
                    "parts_available": packet.avail_basket,
                    "start_date_text": formattedStart,
                    "end_date_text": formattedEnd,
                    "assigned_employees": packet.assigned_employees,
                    "mxi_man_hours": packet.mxi_man_hours,
                    "mxi_man_hours_days": packet.mxi_man_hours_days,
                    "wo_includes_clean_task": packet.wo_includes_clean_task,
                    "need_cert": packet.need_cert,
                    "heavy_bool": packet.heavy_bool,
                    "inventory": packet.inventory,
                });
            });
            setCalendarData(tasks);
        });
    }
    const ReturnStatusColor = (status: string, unassigned: boolean | undefined) => {

        if (unassigned === true) {
            return palette.status.unassigned
        }
        switch (status) {
            case ("COMPLETE"):
                return palette.success;
            case ("ACTV"):
                return palette.secondary;
            case ("PAUSE"):
                return "";
            case ("COMMIT"):
                return palette.status.commit;
            default:
                return palette.status.inwork;
        }
    }

    return (
        <div className="flex flex-1 flex-col min-h-0">
            {loading && <LinearProgress />}
            <div className="flex flex-1 flex-col min-h-0">
                <Tabs
                    tabNames={["Short term", "Long term"]}
                    tabValue={tabValue}
                    onChangeTabValue={setTabValue}
                />
                <div className="ds-filter-bar shrink-0">
                    <div>
                        <SelectMultiple
                            options={Object.values(hangarPlanLineOptions)}
                            onSelectedItem={(e: any) => {
                                setSelectedHangars(e.target.value);
                                safeLocalStorage.setJson("hangarFilters", e.target.value);
                            }}
                            selectedItemsString={selectedHangars}
                            width={200}
                            filter_by_text={"Filter by Hangars"}
                        />
                    </div>
                    <div>
                        <SelectMultiple
                            optionsObject={statusOptions}
                            onSelectedItem={(e: any) => {
                                setSelectedStatusOptions(e.target.value);
                                safeLocalStorage.setJson("statusFilters", e.target.value);
                            }}
                            selectedItems={selectedStatusOptions}
                            width={200}
                            filter_by_text={"Filter by status"}
                        />
                    </div>
                    <div>
                        <SelectMultiple
                            optionsObject={workTypeFilterOptions}
                            onSelectedItem={(e: any) => {
                                setSelectedWorkTypes(e.target.value);
                                safeLocalStorage.setJson("workTypeFilters", e.target.value);
                            }}
                            selectedItems={selectedWorkTypes}
                            width={200}
                            filter_by_text={"Filter by Work Type"}
                        />
                    </div>
                    <Button onClick={() => refresh()} variant="outlined" className="self-end">Refresh</Button>
                </div>
                <div className="w-full flex flex-1 flex-col min-h-0">
                    {tabValue === 0 && <ShortTermHangarPlanTab data={calendarData} hangars={selectedHangars} onRefresh={refresh}/>}
                    {tabValue === 1 && <LongTermHangarPlanTab data={calendarData.filter(x => x.heavy_bool == true)} hangars={selectedHangars} onRefresh={refresh}/>}
                </div>
            </div>
        </div>
    );
}